import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { useSleepTracking } from "@/hooks/useSleepTracking";
import { useWorkouts } from "@/hooks/useWorkouts";
import { useComprehensiveNutrition } from "@/contexts/NutritionContext";
import { useMemo } from "react";
import { Moon, Apple, Activity, Dumbbell } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { WeeklyCalendarWidget } from "@/components/WeeklyCalendarWidget";
import beaconGif from "@/assets/generated (1080 x 1080 px) (2).gif";
import beaconGlowBg from "@/assets/beacon-glow-bg.png";
import straivLogoFull from "@/assets/straiv-logo-full.png";

// Platform check: true if running on web (not native iOS/Android)
const isWebPlatform = !Capacitor.isNativePlatform();
export const Dashboard = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { sleepLogs } = useSleepTracking();
  const { workouts } = useWorkouts();
  const { dailyNutrition, nutritionGoals } = useComprehensiveNutrition();

  const { greeting, recoveryScore, recommendation } = useMemo(() => {
    const hour = new Date().getHours();
    const greetingText = 
      hour < 12 ? "Good Morning" :
      hour < 18 ? "Good Afternoon" :
      "Good Evening";

    const today = new Date().toISOString().split('T')[0];
    const todaySleep = sleepLogs.find(log => log.sleep_date === today);
    const sleepHours = todaySleep?.hours_slept || 0;
    const sleepQuality = todaySleep?.quality_rating || 0;
    
    const completedWorkoutToday = workouts.some(workout => 
      workout.completed_at && 
      new Date(workout.completed_at).toISOString().split('T')[0] === today
    );
    
    const score = sleepHours > 0 
      ? Math.round(((sleepHours / 8) * 0.7 + (sleepQuality / 5) * 0.3) * 100)
      : 0;

    const recommendationText = completedWorkoutToday
      ? "Great work today! Remember to fuel your body with nutritious food and prioritize recovery. Stay hydrated and get quality rest."
      : score >= 85
      ? "Based on your sleep data, today is a perfect day for a high-intensity workout."
      : score >= 70
      ? "You're well-rested. Consider a moderate to high-intensity workout today."
      : score >= 50
      ? "Your recovery is moderate. A light to moderate workout would be ideal."
      : score > 0
      ? "Your body needs more rest. Consider light activity or active recovery today."
      : "What are you straiving for today?";

    return {
      greeting: greetingText,
      recoveryScore: score,
      recommendation: recommendationText
    };
  }, [sleepLogs, workouts]);

  const progressStats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    
    // Recovery (based on sleep)
    const todaySleep = sleepLogs.find(log => log.sleep_date === today);
    const sleepHours = todaySleep?.hours_slept || 0;
    const sleepQuality = todaySleep?.quality_rating || 0;
    const recoveryPercent = sleepHours > 0 
      ? Math.min(Math.round(((sleepHours / 8) * 0.7 + (sleepQuality / 5) * 0.3) * 100), 100)
      : 0;

    // Nutrition
    const caloriesConsumed = dailyNutrition?.totals.calories || 0;
    const caloriesGoal = nutritionGoals?.daily_calories || 2000;
    const proteinConsumed = dailyNutrition?.totals.protein || 0;
    const proteinGoal = nutritionGoals?.daily_protein || 150;
    const nutritionPercent = Math.min(
      Math.round(((caloriesConsumed / caloriesGoal) + (proteinConsumed / proteinGoal)) / 2 * 100), 
      100
    );

    // Activity (workouts)
    const todayWorkouts = workouts.filter(w => 
      w.completed_at && w.completed_at.split('T')[0] === today
    );
    const activityPercent = todayWorkouts.length > 0 ? 100 : 0;

    return { recoveryPercent, nutritionPercent, activityPercent };
  }, [sleepLogs, dailyNutrition, nutritionGoals, workouts]);

  return (
    <div 
      className="flex flex-col items-center w-full px-4 pt-2 pb-6 gap-4 min-h-0 overflow-y-auto md:h-auto md:min-h-full md:justify-start"
    >
      {/* Logo section - Web: Straiv full logo, Native: animated beacon */}
      <div className="w-full flex flex-col justify-center items-center relative flex-shrink-0 overflow-hidden">
        {isWebPlatform ? (
          /* Web-only: Large Straiv official logo replaces beacon */
          <img 
            src={straivLogoFull}
            alt="STRAIV"
            className="h-44 sm:h-56 object-contain my-2 max-w-full"
          />
        ) : (
          /* Native: Beacon with glow - fixed position so glow aligns with top bar */
          <>
            {/* Glow - fixed, centered, extends to top; width constrained to prevent overflow */}
            <img 
              src={beaconGlowBg}
              alt=""
              className="fixed left-1/2 -translate-x-1/2 pointer-events-none z-[1]"
              style={{ 
                top: 0,
                width: 'min(100vw, 450px)',
                maxWidth: '450px',
              }}
            />
            {/* Beacon animation */}
            <div className="relative flex justify-center items-center py-2" style={{ zIndex: 10 }}>
              <img 
                src={beaconGif} 
                alt="STRAIV Beacon" 
                className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
              />
            </div>
          </>
        )}
      </div>

      {/* Greeting Card - Premium Glass */}
      <div 
        className={`w-full max-w-md rounded-3xl relative z-20 flex-shrink-0 backdrop-blur-xl ${isWebPlatform ? '-mt-10' : '-mt-8'}`}
        style={{
          padding: '18px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderTopColor: 'rgba(255,255,255,0.20)',
          borderLeftColor: 'rgba(255,255,255,0.15)',
          borderRightColor: 'rgba(255,255,255,0.05)',
          borderBottomColor: 'rgba(255,255,255,0.05)',
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.35)',
        }}
      >
        <h1 className="text-2xl font-bold text-foreground leading-tight">
          {greeting},
        </h1>
        <h2 className="text-2xl font-bold text-primary mb-1 leading-tight" style={{ textShadow: '0 0 20px rgba(0,255,163,0.3)' }}>
          {profile?.first_name || 'there'}
        </h2>
        <p className="text-xs text-muted-foreground mb-3" style={{ 
          lineHeight: '1.5',
          maxHeight: '4.5em',
          overflow: 'hidden',
          wordWrap: 'break-word'
        }}>
          {recoveryScore > 0 ? (
            <>Your recovery score is {recoveryScore}%. {recommendation}</>
          ) : (
            recommendation
          )}
        </p>
        
        {/* Action Buttons - Frosted Mint */}
        <div className="flex gap-3">
          <Button 
            onClick={() => navigate("/chat")}
            className="flex-1 h-11 font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Ask {profile?.ai_coach_name || 'Arnold'}
          </Button>
          <Button 
            onClick={() => navigate("/workouts")}
            variant="outline"
            className="flex-1 h-11 font-semibold text-foreground backdrop-blur-xl"
            style={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderTopColor: 'rgba(255,255,255,0.20)',
              borderLeftColor: 'rgba(255,255,255,0.15)',
              borderRightColor: 'rgba(255,255,255,0.05)',
              borderBottomColor: 'rgba(255,255,255,0.05)',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.08)',
            }}
          >
            <Dumbbell className="mr-2 h-4 w-4 flex-shrink-0" />
            <span>Log Workout</span>
          </Button>
        </div>
      </div>

      {/* Today's Progress Section */}
      <div className={`w-full max-w-md flex-shrink-0 ${isWebPlatform ? 'mt-0' : 'mt-2'}`}>
        <h3 className="text-lg font-semibold text-center text-foreground mb-4">
          Today&apos;s Progress
        </h3>
        
        <div className="flex justify-between gap-2">
          <ProgressRing 
            label="Recovery" 
            percent={progressStats.recoveryPercent}
            ringColor="hsl(285 100% 55%)"
            textColor="hsl(285 100% 72%)"
            glowColor="hsl(285 100% 55% / 0.6)"
            icon={<Moon className="w-6 h-6" />}
          />
          <ProgressRing 
            label="Nutrition" 
            percent={progressStats.nutritionPercent}
            ringColor="hsl(140 100% 42%)"
            textColor="hsl(140 100% 65%)"
            glowColor="hsl(140 100% 42% / 0.6)"
            icon={<Apple className="w-6 h-6" />}
          />
          <ProgressRing 
            label="Activity" 
            percent={progressStats.activityPercent}
            ringColor="hsl(195 100% 55%)"
            textColor="hsl(195 100% 72%)"
            glowColor="hsl(195 100% 55% / 0.6)"
            icon={<Activity className="w-6 h-6" />}
          />
        </div>
      </div>

      {/* Weekly Calendar Widget */}
      <div className={`w-full max-w-md flex-shrink-0 ${isWebPlatform ? 'mt-0' : 'mt-2'}`}>
        <WeeklyCalendarWidget />
      </div>
    </div>
  );
};

interface ProgressRingProps {
  label: string;
  percent: number;
  ringColor: string; // hsl(...)
  textColor: string; // hsl(...)
  glowColor: string; // hsl(... / alpha)
  icon: React.ReactNode;
}

const ProgressRing = ({
  label,
  percent,
  ringColor,
  textColor,
  glowColor,
  icon,
}: ProgressRingProps) => {
  const radius = 42;
  const strokeWidth = 6;
  const circumference = 2 * Math.PI * radius;

  const safePercent = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const progressOffset = circumference - (safePercent / 100) * circumference;

  // Create a unique gradient id per ring
  const gradId = `ring-grad-${label.toLowerCase()}`;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24 sm:w-28 sm:h-28">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={ringColor} stopOpacity="1" />
              <stop offset="100%" stopColor={ringColor} stopOpacity="0.3" />
            </linearGradient>
          </defs>

          {/* Background track - semi-transparent white */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
          />

          {/* Soft inner glow behind progress */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            stroke={ringColor}
            strokeWidth={strokeWidth + 6}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            opacity={0.12}
            className="transition-all duration-700 ease-out"
          />

          {/* Progress arc with gradient */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            className="transition-all duration-700 ease-out"
            style={{ filter: `drop-shadow(0 0 4px ${glowColor})` }}
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div style={{ color: ringColor }}>{icon}</div>
          <span className="text-base font-bold mt-1" style={{ color: textColor, textShadow: `0 0 12px ${glowColor}` }}>
            {safePercent}%
          </span>
        </div>
      </div>

      <span className="text-sm text-muted-foreground font-medium">{label}</span>
    </div>
  );
};
